import { motion } from 'framer-motion';
import { Player } from '@/lib/gameStore';
import { useEffect } from 'react';
import { useSound } from '@/hooks/useSound';

interface RoundResultsProps {
  selections: any[];
  players: Player[];
  previousTotal: number;
  newTotal: number;
  targetTotal: number;
  onClose: () => void;
}

export default function RoundResults({ 
  selections, 
  players, 
  previousTotal, 
  newTotal, 
  targetTotal,
  onClose 
}: RoundResultsProps) {
  const { playSound } = useSound();
  const movedCloser = Math.abs(newTotal - targetTotal) < Math.abs(previousTotal - targetTotal);

  // Play sound on mount
  useEffect(() => {
    if (movedCloser) {
      playSound('success');
    } else {
      playSound('failure');
    }
  }, [movedCloser, playSound]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);
  const diff = newTotal - previousTotal;

  return (
    <motion.div
      className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="glass-card p-8 rounded-3xl max-w-2xl w-full"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
      >
        <div className="text-center space-y-6">
          {/* Header */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="text-4xl font-black text-foreground mb-2">
              {movedCloser ? '🔥 GETTING CLOSER! 🔥' : '💀 UH OH... 💀'}
            </div>
            <div className="text-2xl font-bold text-muted-foreground">
              {previousTotal} → {newTotal} ({diff > 0 ? '+' : ''}{diff})
            </div>
          </motion.div>

          {/* Selections */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {selections.map((selection, index) => {
              const player = players.find(p => p.id === selection.player_id);
              if (!player) return null;

              const isClutch = Math.abs(selection.selected_value) <= 5;
              const isBad = Math.abs(selection.selected_value) > 30;

              return (
                <motion.div
                  key={selection.id}
                  className="glass-card p-4 rounded-2xl"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  <div className="text-sm font-bold text-muted-foreground mb-2">
                    {player.name}
                  </div>
                  <div className="text-4xl font-black text-primary mb-1">
                    {selection.selected_value}
                  </div>
                  <div className="text-2xl">
                    {isClutch ? '🔥' : isBad ? '💀' : '👍'}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Progress indicator */}
          <motion.div
            className="h-2 bg-border rounded-full overflow-hidden"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.5 }}
          >
            <motion.div
              className={`h-full ${movedCloser ? 'bg-chaos-green' : 'bg-chaos-red'}`}
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 2, delay: 0.6 }}
            />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
